import { SimulatorType } from "@/types/api/SimulatorType"
import type { FormData } from "@/types/FormData"
import {
  isOpenAIRealtimeApiKeySource,
  OPENAI_REALTIME_API_KEY_SOURCE,
} from "@/utils/simulation/simulatorFields"
import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

interface AgentValidationResult {
  success: boolean
  error?: string
}

export function parseStrictInteger(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isInteger(value) ? value : null
  }
  if (typeof value === "string") {
    const trimmed = value.trim()
    if (/^-?\d+$/.test(trimmed)) {
      return Number(trimmed)
    }
  }
  return null
}

function validateSimulationTimeout(
  metadata: Record<string, unknown>,
): AgentValidationResult | null {
  const timeoutSeconds = metadata.simulation_timeout_seconds
  if (timeoutSeconds === undefined || timeoutSeconds === "") {
    return null
  }
  const timeoutValue = parseStrictInteger(timeoutSeconds)
  if (timeoutValue === null || timeoutValue < 1 || timeoutValue > 1800) {
    return {
      success: false,
      error:
        "Simulation timeout must be an integer between 1 and 1800 seconds.",
    }
  }
  return null
}

function validateTemperature(
  temperature: unknown,
): AgentValidationResult | null {
  if (temperature === undefined || temperature === "") {
    return null
  }
  const temperatureValue =
    typeof temperature === "number"
      ? temperature
      : parseFloat(stringifyUnknownValue(temperature))
  if (
    Number.isNaN(temperatureValue) ||
    temperatureValue < 0 ||
    temperatureValue > 2
  ) {
    return {
      success: false,
      error: "Temperature must be a number between 0 and 2.",
    }
  }
  return null
}

// The optional JSON blob fields are free-text textareas, so a user can leave
// whitespace behind. Treat a whitespace-only value as "not provided", matching
// getJsonArrayError/getJsonObjectError in realtimeFieldValidation — otherwise
// the field shows no inline error and then blocks the save.
function isBlankJsonFieldValue(value: unknown): boolean {
  return (
    value === undefined || (typeof value === "string" && value.trim() === "")
  )
}

function validateJsonArray(
  value: unknown,
  fieldLabel: string,
): AgentValidationResult | null {
  if (isBlankJsonFieldValue(value)) {
    return null
  }
  if (Array.isArray(value)) {
    return null
  }
  if (typeof value === "string") {
    try {
      const parsed: unknown = JSON.parse(value)
      if (Array.isArray(parsed)) {
        return null
      }
      return {
        success: false,
        error: `${fieldLabel} must be a JSON array.`,
      }
    } catch {
      return {
        success: false,
        error: `${fieldLabel} must be valid JSON.`,
      }
    }
  }
  return {
    success: false,
    error: `${fieldLabel} must be a JSON array.`,
  }
}

function validateJsonObject(
  value: unknown,
  fieldLabel: string,
): AgentValidationResult | null {
  if (isBlankJsonFieldValue(value)) {
    return null
  }
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return null
  }
  if (typeof value === "string") {
    try {
      const parsed: unknown = JSON.parse(value)
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        !Array.isArray(parsed)
      ) {
        return null
      }
      return {
        success: false,
        error: `${fieldLabel} must be a JSON object.`,
      }
    } catch {
      return {
        success: false,
        error: `${fieldLabel} must be valid JSON.`,
      }
    }
  }
  return {
    success: false,
    error: `${fieldLabel} must be a JSON object.`,
  }
}

export function validateRealtimeAgent(
  simulatorType: SimulatorType | null,
  metadataValue: FormData["metadata"] | undefined,
): AgentValidationResult | null {
  if (
    simulatorType !== SimulatorType.GEMINI_REALTIME &&
    simulatorType !== SimulatorType.OPENAI_REALTIME &&
    simulatorType !== SimulatorType.GROK_REALTIME &&
    simulatorType !== SimulatorType.DEEPGRAM_REALTIME
  ) {
    return null
  }
  if (!metadataValue) {
    const provider =
      simulatorType === SimulatorType.GEMINI_REALTIME
        ? "Gemini Live"
        : simulatorType === SimulatorType.OPENAI_REALTIME
          ? "OpenAI Realtime"
          : simulatorType === SimulatorType.GROK_REALTIME
            ? "Grok Realtime"
            : "Deepgram Voice Agent"
    return {
      success: false,
      error: `Metadata is required for ${provider} agents.`,
    }
  }

  const metadata: Record<string, unknown> = metadataValue
  if (simulatorType === SimulatorType.GEMINI_REALTIME) {
    const apiKey = metadata.gemini_realtime_api_key
    if (typeof apiKey !== "string" || apiKey.trim() === "") {
      return {
        success: false,
        error:
          "Google AI API Key is required for Gemini Live agents. Supports AIzaSy… or AQ.… formats.",
      }
    }
    const temperatureError = validateTemperature(
      metadata.gemini_realtime_temperature,
    )
    if (temperatureError) {
      return temperatureError
    }
  }

  if (simulatorType === SimulatorType.OPENAI_REALTIME) {
    const rawApiKeySource = metadata.openai_realtime_api_key_source
    if (
      rawApiKeySource !== undefined &&
      !isOpenAIRealtimeApiKeySource(rawApiKeySource)
    ) {
      return {
        success: false,
        error:
          "OpenAI API Key Source must be 'customer_provided' or 'coval_managed'.",
      }
    }
    const apiKeySource =
      rawApiKeySource ?? OPENAI_REALTIME_API_KEY_SOURCE.CUSTOMER_PROVIDED
    const apiKey = metadata.openai_realtime_api_key
    if (
      apiKeySource === OPENAI_REALTIME_API_KEY_SOURCE.CUSTOMER_PROVIDED &&
      (typeof apiKey !== "string" || apiKey.trim() === "")
    ) {
      return {
        success: false,
        error: "OpenAI API Key is required for OpenAI Realtime agents.",
      }
    }
    const temperatureError = validateTemperature(
      metadata.openai_realtime_temperature,
    )
    if (temperatureError) {
      return temperatureError
    }
  }

  if (simulatorType === SimulatorType.GROK_REALTIME) {
    const apiKey = metadata.grok_realtime_api_key
    if (typeof apiKey !== "string" || apiKey.trim() === "") {
      return {
        success: false,
        error: "xAI API Key is required for Grok Realtime agents.",
      }
    }
  }

  if (simulatorType === SimulatorType.DEEPGRAM_REALTIME) {
    const apiKey = metadata.deepgram_realtime_api_key
    if (typeof apiKey !== "string" || apiKey.trim() === "") {
      return {
        success: false,
        error: "Deepgram API Key is required for Deepgram Voice Agent agents.",
      }
    }
    const functionsError = validateJsonArray(
      metadata.deepgram_realtime_functions,
      "Agent Tool Definitions",
    )
    if (functionsError) {
      return functionsError
    }
    const settingsError = validateJsonObject(
      metadata.deepgram_realtime_settings,
      "Deepgram Settings Override",
    )
    if (settingsError) {
      return settingsError
    }
  }

  const timeoutError = validateSimulationTimeout(metadata)
  return timeoutError ?? { success: true }
}
